import React, { useEffect, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addDays,
  subDays,
  startOfDay,
  isSameDay,
  startOfMonth,
  endOfMonth,
  parseISO,
  setHours,
  setMinutes,
  addMinutes,
  isValid,
  set,
} from "date-fns";
import fr from "date-fns/locale/fr";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Icônes réelles
import {
  BsChevronLeft,
  BsChevronRight,
  BsSliders,
  BsGear,
  BsCalendarCheck,
  BsArrowClockwise,
  BsChevronDown,
  BsSearch,
} from "react-icons/bs";
import { TbUsersGroup } from "react-icons/tb";
// Icônes pour les événements spéciaux
import {
  MdOutlineClose,
  MdOutlineCoffee,
  MdAccessTime,
  MdEventAvailable,
} from "react-icons/md";
import useReservationStore from "../store/reservationStore";
import useSalonStore from "../store/salonStore";
import ReservationItem from "./ReservationItem";

const locales = { fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

export default function AgendaCalendar() {
  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchDate, setSearchDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localEvents, setLocalEvents] = useState([]);

  const {
    reservationsByDateRange,
    isLoadingByDateRange,
    getReservationsByDateRange,
  } = useReservationStore();
  const { currentSalon, getSalonById } = useSalonStore();

  const getClosureEvents = useCallback(() => {
    if (!currentSalon) return [];

    const events = [];
    const startDate = startOfMonth(currentDate);
    const endDate = endOfMonth(currentDate);

    const closedDaysMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    currentSalon.closedDays?.forEach((day) => {
      if (closedDaysMap.hasOwnProperty(day)) {
        const dayOfWeek = closedDaysMap[day];
        let currentDay = startDate;

        while (currentDay <= endDate) {
          if (currentDay.getDay() === dayOfWeek) {
            events.push({
              id: `closed-${day}-${format(currentDay, "yyyy-MM-dd")}`,
              title: "Fermé",
              start: startOfDay(currentDay),
              end: addMinutes(startOfDay(currentDay), 1439),
              isClosure: true,
              type: "closed_day",
              resource: { reason: `Fermeture ${day}` },
            });
          }
          currentDay = addDays(currentDay, 1);
        }
      }
    });

    currentSalon.closedDays?.forEach((day) => {
      if (day.match(/^\d{4}-\d{2}-\d{2}$/)) {
        try {
          const closedDate = parseISO(day);
          if (closedDate >= startDate && closedDate <= endDate) {
            events.push({
              id: `closed-special-${day}`,
              title: "Fermé Spécial",
              start: startOfDay(closedDate),
              end: addMinutes(startOfDay(closedDate), 1439),
              isClosure: true,
              type: "special_closed",
              resource: { reason: "Fermeture exceptionnelle" },
            });
          }
        } catch (error) {
          console.warn(`Date de fermeture invalide: ${day}`);
        }
      }
    });

    return events;
  }, [currentSalon, currentDate]);

  const getBreakEvents = useCallback(() => {
    if (!currentSalon || !currentSalon.breakStart || !currentSalon.breakEnd)
      return [];

    const events = [];
    const startDate = startOfMonth(currentDate);
    const endDate = endOfMonth(currentDate);
    let currentDay = startDate;

    while (currentDay <= endDate) {
      const dayName = format(currentDay, "EEEE").toLowerCase();
      const dateString = format(currentDay, "yyyy-MM-dd");
      const isClosed =
        currentSalon.closedDays?.includes(dayName) ||
        currentSalon.closedDays?.includes(dateString);

      if (!isClosed) {
        try {
          const [breakStartHours, breakStartMinutes] = currentSalon.breakStart
            .split(":")
            .map(Number);
          const [breakEndHours, breakEndMinutes] = currentSalon.breakEnd
            .split(":")
            .map(Number);

          const breakStart = setMinutes(
            setHours(currentDay, breakStartHours),
            breakStartMinutes,
          );
          const breakEnd = setMinutes(
            setHours(currentDay, breakEndHours),
            breakEndMinutes,
          );

          events.push({
            id: `break-${format(currentDay, "yyyy-MM-dd")}`,
            title: "Pause",
            start: breakStart,
            end: breakEnd,
            isBreak: true,
            type: "break",
            resource: {
              reason: "Pause déjeuner",
              duration: `${currentSalon.breakStart} - ${currentSalon.breakEnd}`,
            },
          });
        } catch (error) {
          console.warn("Erreur création événement pause:", error);
        }
      }
      currentDay = addDays(currentDay, 1);
    }

    return events;
  }, [currentSalon, currentDate]);

  const events = React.useMemo(() => {
    const reservationEvents = reservationsByDateRange.map((rdv) => {
      const start = parseISO(rdv.date);
      const duration = rdv.estimatedDuration || 30;
      const end = new Date(start.getTime() + duration * 60000);

      const assignedWorkers =
        rdv.assignments?.map(
          (assignment) =>
            `${assignment.employee.prenom} ${assignment.employee.nom}`,
        ) || [];

      const clientName = rdv.client
        ? `${rdv.client.nom + " " + rdv.client.prenom}`
        : rdv.anonymous_data
          ? rdv.anonymous_data.name
          : "Client non répertorié";

      return {
        ...rdv,
        title: `${rdv.appointmentServices?.[0]?.service?.nom || "Service"} (${
          clientName
        })`,
        start,
        end,
        status: rdv.status,
        resource: rdv,
        assignedWorkers,
        workerCount: assignedWorkers.length,
        type: "reservation",
      };
    });

    const closureEvents = getClosureEvents();
    const breakEvents = getBreakEvents();

    return [...reservationEvents, ...closureEvents, ...breakEvents];
  }, [reservationsByDateRange, getClosureEvents, getBreakEvents]);

  useEffect(() => {
    // Toujours synchroniser : après une annulation, la liste peut devenir vide
    setLocalEvents(events);
  }, [reservationsByDateRange, events]);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    setShowViewDropdown(false);
    setShowMobileMenu(false);
  }, []);

  const handleNavigate = useCallback(
    (action) => {
      switch (action) {
        case "PREV":
          if (currentView === "day") setCurrentDate(subDays(currentDate, 1));
          else if (currentView === "week")
            setCurrentDate(subWeeks(currentDate, 1));
          else if (currentView === "month")
            setCurrentDate(subMonths(currentDate, 1));
          break;
        case "NEXT":
          if (currentView === "day") setCurrentDate(addDays(currentDate, 1));
          else if (currentView === "week")
            setCurrentDate(addWeeks(currentDate, 1));
          else if (currentView === "month")
            setCurrentDate(addMonths(currentDate, 1));
          break;
        case "TODAY":
          setCurrentDate(new Date());
          break;
        default:
          break;
      }
    },
    [currentView, currentDate],
  );

  const handleSelectSlot = useCallback(({ start }) => {
    setCurrentDate(start);
    setCurrentView("day");
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setCurrentDate(event.start);
    setCurrentView("day");
  }, []);

  const handleSearchDate = useCallback(async () => {
    if (!searchDate) return;

    try {
      setIsLoading(true);
      const parsedDate = parseISO(searchDate);

      if (!isValid(parsedDate)) {
        alert("Date invalide. Format attendu: YYYY-MM-DD");
        return;
      }

      setCurrentDate(parsedDate);
      setCurrentView("day");

      const startDate = format(parsedDate, "yyyy-MM-dd");
      const endDate = format(parsedDate, "yyyy-MM-dd");

      await getReservationsByDateRange(startDate, endDate);

      setShowSearch(false);
      setSearchDate("");
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      alert("Erreur lors de la recherche de date");
    } finally {
      setIsLoading(false);
    }
  }, [searchDate, getReservationsByDateRange]);

  const formatDateDisplay = useCallback(() => {
    switch (currentView) {
      case "day":
        return format(currentDate, "d MMM yyyy", { locale: fr });
      case "week":
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        const end = addDays(start, 6);
        return `${format(start, "d MMM", { locale: fr })} - ${format(
          end,
          "d MMM yyyy",
          { locale: fr },
        )}`;
      case "month":
        return format(currentDate, "MMM yyyy", { locale: fr });
      default:
        return format(currentDate, "d MMM yyyy", { locale: fr });
    }
  }, [currentView, currentDate]);

  const viewOptions = [
    { value: "day", label: "Jour" },
    { value: "week", label: "Semaine" },
    { value: "month", label: "Mois" },
  ];

  useEffect(() => {
    getSalonById(1);
  }, [getSalonById]);

  const loadReservations = useCallback(async () => {
      let startDate, endDate;

      switch (currentView) {
        case "day":
          startDate = format(currentDate, "yyyy-MM-dd");
          endDate = format(addDays(currentDate, 1), "yyyy-MM-dd");
          break;
        case "week":
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
          const weekEnd = addDays(weekStart, 6);
          startDate = format(weekStart, "yyyy-MM-dd");
          endDate = format(weekEnd, "yyyy-MM-dd");
          break;
        case "month":
        default:
          const monthStart = startOfMonth(currentDate);
          const monthEnd = endOfMonth(currentDate);
          startDate = format(monthStart, "yyyy-MM-dd");
          endDate = format(monthEnd, "yyyy-MM-dd");
          break;
      }

      await getReservationsByDateRange(startDate, endDate);
  }, [currentDate, currentView, getReservationsByDateRange]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const getFilteredEvents = useCallback(() => {
    const eventsToUse = localEvents.length > 0 ? localEvents : events;

    switch (currentView) {
      case "month":
        return eventsToUse.filter((event) => !event.isBreak);
      case "agenda":
        return eventsToUse.filter((event) => !event.isBreak);
      default:
        return eventsToUse;
    }
  }, [currentView, events, localEvents]);

  const getDayEvents = useCallback(() => {
    const eventsToUse = localEvents.length > 0 ? localEvents : events;
    return eventsToUse.filter(
      (event) =>
        isSameDay(event.start, currentDate) && event.type === "reservation",
    );
  }, [events, localEvents, currentDate]);

  const isDayClosed = useCallback(() => {
    const eventsToUse = localEvents.length > 0 ? localEvents : events;
    return eventsToUse.some(
      (event) => event.isClosure && isSameDay(event.start, currentDate),
    );
  }, [events, localEvents, currentDate]);

  const getDayBreaks = useCallback(() => {
    const eventsToUse = localEvents.length > 0 ? localEvents : events;
    return eventsToUse.filter(
      (event) => event.isBreak && isSameDay(event.start, currentDate),
    );
  }, [events, localEvents, currentDate]);

  const getMinTime = useCallback(() => {
    if (!currentSalon || !currentSalon.openingTime) {
      return setHours(setMinutes(new Date(), 0), 6);
    }

    const [hours, minutes] = currentSalon.openingTime.split(":").map(Number);
    return setHours(setMinutes(new Date(), minutes), hours);
  }, [currentSalon]);

  const getMaxTime = useCallback(() => {
    if (!currentSalon || !currentSalon.closingTime) {
      return setHours(setMinutes(new Date(), 0), 22);
    }

    const [hours, minutes] = currentSalon.closingTime.split(":").map(Number);
    return setHours(setMinutes(new Date(), minutes), hours);
  }, [currentSalon]);

  const createTimeSlots = useCallback(() => {
    if (
      !currentSalon ||
      !currentSalon.openingTime ||
      !currentSalon.closingTime
    ) {
      const slots = [];
      let currentTime = set(
        set(currentDate, {
          hours: 6,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        }),
        {},
      );

      const endTime = set(
        set(currentDate, {
          hours: 22,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        }),
        {},
      );

      while (currentTime <= endTime) {
        slots.push(new Date(currentTime));
        currentTime = addMinutes(currentTime, 30);
      }
      return slots;
    }

    const slots = [];
    const [openHours, openMinutes] = currentSalon.openingTime
      .split(":")
      .map(Number);
    const [closeHours, closeMinutes] = currentSalon.closingTime
      .split(":")
      .map(Number);

    let currentTime = set(
      set(currentDate, {
        hours: openHours,
        minutes: openMinutes,
        seconds: 0,
        milliseconds: 0,
      }),
      {},
    );

    const endTime = set(
      set(currentDate, {
        hours: closeHours,
        minutes: closeMinutes,
        seconds: 0,
        milliseconds: 0,
      }),
      {},
    );

    while (currentTime <= endTime) {
      slots.push(new Date(currentTime));
      currentTime = addMinutes(currentTime, 30);
    }

    return slots;
  }, [currentSalon, currentDate]);

  const customEventPropGetter = useCallback((event) => {
    if (event.isClosure) {
      return {
        style: {
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "2px solid #fecaca",
          borderRadius: "4px",
          padding: "2px 4px",
          fontSize: "10px",
          fontWeight: "500",
          margin: "1px",
          opacity: 0.8,
        },
      };
    }

    if (event.isBreak) {
      return {
        style: {
          backgroundColor: "#fffbeb",
          color: "#d97706",
          border: "2px solid #fed7aa",
          borderRadius: "4px",
          padding: "2px 4px",
          fontSize: "10px",
          fontWeight: "500",
          margin: "1px",
          opacity: 0.7,
        },
      };
    }

    let backgroundColor = "#f3f4f6";
    let color = "#374151";

    if (event.status) {
      switch (event.status) {
        case "PENDING":
          backgroundColor = "#fef3c7";
          color = "#92400e";
          break;
        case "COMPLETED":
          backgroundColor = "#d1fae5";
          color = "#065f46";
          break;
        case "CANCELLED":
          backgroundColor = "#fee2e2";
          color = "#991b1b";
          break;
        default:
          backgroundColor = "#f3f4f6";
          color = "#374151";
      }
    }

    return {
      style: {
        backgroundColor: backgroundColor,
        color: color,
        borderRadius: "4px",
        border: "none",
        padding: "2px 4px",
        fontSize: "10px",
        fontWeight: "500",
        margin: "1px",
      },
    };
  }, []);

  const CustomEvent = useCallback(({ event }) => {
    if (event.isClosure) {
      return (
        <div className="flex items-center gap-1 text-xs">
          <MdOutlineClose size={12} className="text-red-600" />
          <span className="truncate">{event.title}</span>
        </div>
      );
    }

    if (event.isBreak) {
      return (
        <div className="flex items-center gap-1 text-xs">
          <MdOutlineCoffee size={12} className="text-orange-600" />
          <span className="truncate">{event.title}</span>
        </div>
      );
    }

    return <div className="text-xs truncate">{event.title}</div>;
  }, []);

  const CustomDayView = useCallback(() => {
    const dayEvents = getDayEvents();
    const dayBreaks = getDayBreaks();
    const isClosed = isDayClosed();
    const isToday = isSameDay(currentDate, new Date());

    if (isClosed) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <MdOutlineClose size={64} className="text-red-500 mb-4" />
          <h3 className="font-poppins font-semibold text-[18px] text-[#222] mb-2">
            Fermé
          </h3>
          <p className="font-nunito text-[14px] text-[#333] text-lg">
            Le salon est fermé aujourd'hui
          </p>
        </div>
      );
    }

    const allDayEvents = [
      ...dayEvents.map((event) => ({
        ...event,
        eventType: "reservation",
      })),
      ...dayBreaks.map((breakEvent) => ({
        ...breakEvent,
        eventType: "break",
      })),
    ];

    const sortedEvents = allDayEvents.sort((a, b) => a.start - b.start);

    if (sortedEvents.length === 0) {
      return (
        <div className="h-full bg-white">
          <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MdEventAvailable size={24} className="text-secondary-600" />
                <div>
                  <h2 className="font-poppins font-semibold text-[18px] text-[#222]">
                    {format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
                  </h2>
                  <p className="font-nunito text-[14px] text-[#333] flex items-center gap-1">
                    <MdAccessTime size={16} />
                    {currentSalon?.openingTime || "08:00"} -{" "}
                    {currentSalon?.closingTime || "18:00"}
                  </p>
                </div>
              </div>
              {isToday && (
                <span className="bg-secondary-100 text-secondary-800 px-3 py-1 rounded-full font-nunito text-[14px] font-semibold">
                  Aujourd'hui
                </span>
              )}
            </div>

            <div className="flex gap-4 mt-3">
              <div className="text-center">
                <div className="font-poppins font-bold text-[16px] text-[#111]">
                  0
                </div>
                <div className="font-nunito text-[14px] text-[#333]">
                  Total RDV
                </div>
              </div>
              <div className="text-center">
                <div className="font-poppins font-bold text-[16px] text-[#111]">
                  0
                </div>
                <div className="font-nunito text-[14px] text-[#333]">
                  En attente
                </div>
              </div>
              <div className="text-center">
                <div className="font-poppins font-bold text-[16px] text-[#111]">
                  0
                </div>
                <div className="font-nunito text-[14px] text-[#333]">
                  Terminés
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center h-64 text-center">
            <MdEventAvailable size={48} className="text-gray-400 mb-4" />
            <h3 className="font-poppins font-semibold text-[18px] text-[#222] mb-2">
              Aucun événement programmé
            </h3>
            <p className="font-nunito text-[14px] text-[#333]">
              Aucun rendez-vous ou pause n'est prévu pour aujourd'hui
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full bg-white">
        <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MdEventAvailable size={24} className="text-secondary-600" />
              <div>
                <h2 className="font-poppins font-semibold text-[18px] text-[#222]">
                  {format(currentDate, "EEEE d MMMM yyyy", { locale: fr })}
                </h2>
                <p className="font-nunito text-[14px] text-[#333] flex items-center gap-1">
                  <MdAccessTime size={16} />
                  {currentSalon?.openingTime || "08:00"} -{" "}
                  {currentSalon?.closingTime || "18:00"}
                </p>
              </div>
            </div>
            {isToday && (
              <span className="bg-secondary-100 text-secondary-800 px-3 py-1 rounded-full font-nunito text-[14px] font-semibold">
                Aujourd'hui
              </span>
            )}
          </div>

          <div className="flex gap-4 mt-3">
            <div className="text-center">
              <div className="font-poppins font-bold text-[16px] text-[#111]">
                {dayEvents.length}
              </div>
              <div className="font-nunito text-[14px] text-[#333]">
                Total RDV
              </div>
            </div>
            <div className="text-center">
              <div className="font-poppins font-bold text-[16px] text-[#111]">
                {dayEvents.filter((e) => e.status === "PENDING").length}
              </div>
              <div className="font-nunito text-[14px] text-[#333]">
                En attente
              </div>
            </div>
            <div className="text-center">
              <div className="font-poppins font-bold text-[16px] text-[#111]">
                {dayEvents.filter((e) => e.status === "COMPLETED").length}
              </div>
              <div className="font-nunito text-[14px] text-[#333]">
                Terminés
              </div>
            </div>
          </div>
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(70vh - 120px)" }}
        >
          <div className="min-h-full p-4">
            {sortedEvents.map((event, index) => (
              <>
                <div key={event.id} className="mb-4 last:mb-0">
                  <div className="flex items-start gap-4">
                    <div className="w-16 py-2 text-right font-poppins font-medium text-[15px] text-[#444] flex-shrink-0">
                      {format(event.start, "HH:mm")}
                    </div>

                    {event.eventType === "reservation" ? (
                      <div
                        className={`flex-1 border-l-4 ${
                          event.status === "PENDING"
                            ? "bg-yellow-50 border-yellow-400"
                            : event.status === "CONFIRMED"
                              ? "bg-emerald-50 border-emerald-400"
                              : event.status === "COMPLETED"
                                ? "bg-green-50 border-green-400"
                                : "bg-red-50 border-red-400"
                        }`}
                      >
                        <ReservationItem
                          key={event.id}
                          item={event}
                          withAssign={true}
                          onUpdated={loadReservations}
                        />
                      </div>
                    ) : (
                      <div
                        className={`flex-1 p-4 border-l-4 ${
                          event.eventType === "break"
                            ? "bg-orange-50 border-orange-400"
                            : event.status === "PENDING"
                              ? "bg-yellow-50 border-yellow-400"
                              : event.status === "COMPLETED"
                                ? "bg-green-50 border-green-400"
                                : "bg-red-50 border-red-400"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {event.eventType === "break" ? (
                                <MdOutlineCoffee
                                  size={18}
                                  className="text-orange-600"
                                />
                              ) : (
                                <MdEventAvailable
                                  size={18}
                                  className={
                                    event.status === "PENDING"
                                      ? "text-yellow-600"
                                      : event.status === "COMPLETED"
                                        ? "text-green-600"
                                        : "text-red-600"
                                  }
                                />
                              )}
                              <div className="font-poppins font-semibold text-[18px] text-[#222]">
                                {event.title}
                              </div>
                            </div>

                            <div className="font-nunito text-[14px] text-[#333] mb-2">
                              {format(event.start, "HH:mm")} -{" "}
                              {format(event.end, "HH:mm")}
                              <span className="mx-2">•</span>
                              {event.eventType === "break"
                                ? "Pause déjeuner"
                                : `${
                                    event.resource?.estimatedDuration || 30
                                  } min`}
                            </div>

                            {event.eventType === "reservation" &&
                              event.assignedWorkers &&
                              event.assignedWorkers.length > 0 && (
                                <div className="mt-2">
                                  <div className="font-poppins font-medium text-[15px] text-[#444] mb-1">
                                    Workers assignés:
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {event.assignedWorkers.map(
                                      (worker, workerIndex) => (
                                        <span
                                          key={workerIndex}
                                          className="bg-green-100 text-green-700 px-2 py-1 font-nunito text-[14px] font-semibold"
                                        >
                                          {worker}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>

                          {event.eventType === "reservation" && (
                            <span
                              className={`px-3 py-1 rounded-full font-nunito text-[14px] font-semibold ${
                                event.status === "PENDING"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : event.status === "COMPLETED"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {event.status === "PENDING"
                                ? "En attente"
                                : event.status === "CONFIRMED"
                                  ? "Confirmé"
                                  : event.status === "COMPLETED"
                                    ? "Terminé"
                                    : "Annulé"}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {index < sortedEvents.length - 1 && (
                    <div className="flex items-center gap-4 mt-2 mb-2">
                      <div className="w-16 text-right font-nunito text-[14px] text-[#333] flex-shrink-0">
                        ↓
                      </div>
                      <div className="flex-1 border-t border-gray-200"></div>
                    </div>
                  )}
                </div>
              </>
            ))}
          </div>
        </div>
      </div>
    );
  }, [
    currentDate,
    currentSalon,
    getDayEvents,
    getDayBreaks,
    isDayClosed,
    loadReservations,
  ]);

  const getDayEventsStats = useCallback(
    (date) => {
      const eventsToUse = localEvents.length > 0 ? localEvents : events;
      const dayEvents = eventsToUse.filter(
        (event) => isSameDay(event.start, date) && event.type === "reservation",
      );
      return {
        total: dayEvents.length,
        pending: dayEvents.filter((event) => event.status === "PENDING").length,
        completed: dayEvents.filter((event) => event.status === "COMPLETED")
          .length,
        cancelled: dayEvents.filter((event) => event.status === "CANCELLED")
          .length,
      };
    },
    [events, localEvents],
  );

  const CustomMonthView = useCallback(
    ({ date }) => {
      const dayStats = getDayEventsStats(date);
      const isToday = isSameDay(date, new Date());
      const eventsToUse = localEvents.length > 0 ? localEvents : events;
      const isClosed = eventsToUse.some(
        (event) => event.isClosure && isSameDay(event.start, date),
      );

      const handleDateClick = () => {
        setCurrentDate(date);
        setCurrentView("day");
      };

      return (
        <div
          className={`h-full w-full p-1 ${isToday ? "bg-secondary-50" : ""} ${
            isClosed ? "bg-red-50" : ""
          } cursor-pointer hover:bg-gray-50 transition-colors`}
          onClick={handleDateClick}
        >
          <div className="flex justify-between items-center mb-1">
            <span
              className={`font-poppins font-medium text-[15px] ${
                isToday
                  ? "text-secondary-600"
                  : isClosed
                    ? "text-red-600"
                    : "text-[#222]"
              }`}
            >
              {format(date, "d")}
            </span>
            {dayStats.total > 0 && (
              <span className="font-nunito text-[14px] font-semibold bg-secondary-100 text-secondary-800 rounded-full px-1 py-0.5 min-w-[16px] text-center">
                {dayStats.total}
              </span>
            )}
            {isClosed && (
              <span className="font-nunito text-[14px] font-semibold bg-red-100 text-red-800 rounded-full px-1 py-0.5">
                <MdOutlineClose size={10} />
              </span>
            )}
          </div>

          {dayStats.total > 0 && (
            <div className="flex justify-center gap-1 mt-1">
              {dayStats.pending > 0 && (
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
              )}
              {dayStats.completed > 0 && (
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              )}
              {dayStats.cancelled > 0 && (
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
              )}
            </div>
          )}
        </div>
      );
    },
    [events, localEvents, getDayEventsStats],
  );

  return (
    <div className="pb-4">
      {(isLoadingByDateRange || isLoading) && (
        <div className="fixed top-0 left-0 w-full h-1 bg-[#FF4081] z-50">
          <div className="h-full bg-[#FF4081]/80 animate-pulse"></div>
        </div>
      )}

      {currentSalon ? (
        <div className="bg-secondary-50 border-b border-secondary-200 px-3 py-2 font-nunito text-[14px] text-secondary-700">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <span className="font-poppins font-semibold text-[18px] text-[#222]">
              {currentSalon.designation}
            </span>
            <span className="font-nunito text-[14px] text-[#333] flex items-center gap-1">
              <MdAccessTime size={12} />
              {currentSalon.openingTime} - {currentSalon.closingTime}
            </span>
            {currentSalon.breakStart && (
              <span className="font-nunito text-[14px] text-[#333] flex items-center gap-1">
                <MdOutlineCoffee size={12} />
                Pause: {currentSalon.breakStart} - {currentSalon.breakEnd}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border-b border-yellow-200 px-3 py-2 font-nunito text-[12px] text-yellow-700">
          <div className="flex items-center gap-2">
            <span>
              ⚠️ Aucun salon sélectionné - Affichage avec horaires par défaut
              (6h-22h)
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between bg-white border-b border-gray-200 p-3 ">
        <div className="flex items-center justify-between mb-3 gap-3 w-full">
          <button
            onClick={() => handleNavigate("TODAY")}
            className="font-nunito font-semibold text-[14px] hidden md:flex px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            Aujourd'hui
          </button>

          <div className="flex items-center border border-gray-300 overflow-hidden flex-shrink-0 flex-1 min-w-0">
            <button
              onClick={() => handleNavigate("PREV")}
              className="p-1.5 hover:bg-gray-100 transition-colors border-r border-gray-300 flex-shrink-0"
            >
              <BsChevronLeft size={14} />
            </button>
            <button className="px-2 py-1.5 font-poppins font-medium text-[15px] text-[#444] hover:bg-gray-50 truncate text-center flex-1">
              {formatDateDisplay()}
            </button>
            <button
              onClick={() => handleNavigate("NEXT")}
              className="p-1.5 hover:bg-gray-100 transition-colors border-l border-gray-300 flex-shrink-0"
            >
              <BsChevronRight size={14} />
            </button>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
              title="Rechercher une date"
            >
              <BsSearch size={16} />
            </button>

            <button
              onClick={() => setShowTeamManagement(!showTeamManagement)}
              className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors font-nunito text-[14px] flex-shrink-0"
            >
              <TbUsersGroup size={14} />
              <span className="hidden md:flex">Équipe</span>
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => handleNavigate("TODAY")}
            className="flex md:hidden px-3 py-1.5 border border-gray-300 font-nunito font-semibold text-[14px] text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            Aujourd'hui
          </button>

          {showSearch && (
            <div className="flex items-center gap-2 ml-4 flex-1 max-w-md">
              <input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 font-nunito text-[14px] text-[#333] flex-1"
                placeholder="YYYY-MM-DD"
              />
              <button
                onClick={handleSearchDate}
                disabled={isLoading}
                className="px-3 py-1.5 bg-[#FF4081] text-white font-nunito font-semibold text-[14px] hover:bg-[#FF4081]/90 transition-colors disabled:opacity-50"
              >
                {isLoading ? "..." : "Aller"}
              </button>
              <button
                onClick={() => setShowSearch(false)}
                className="p-1.5 text-gray-600 hover:text-gray-800"
              >
                ×
              </button>
            </div>
          )}

          <div className="flex items-center gap-1 ml-auto">
            {/* Bouton sans action, masqué en attendant son implémentation
            <button
              className="p-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
              title="Rafraîchir"
            >
              <BsArrowClockwise size={14} />
            </button>
            */}

            <div className="relative">
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                className="flex items-center gap-1 py-1.5 px-2 border border-gray-300 font-poppins font-medium text-[15px] text-[#444] hover:bg-gray-50 transition-colors min-w-[80px] justify-between rounded"
              >
                <span className="truncate">
                  {viewOptions.find((opt) => opt.value === currentView)
                    ?.label || "Mois"}
                </span>
                <BsChevronDown size={10} />
              </button>

              {showViewDropdown && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-40 min-w-[100px] rounded">
                  {viewOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`block w-full text-left px-3 py-2 font-nunito text-[14px] hover:bg-gray-50 ${
                        currentView === option.value
                          ? "bg-[#FF4081]/10 text-[#FF4081]"
                          : "text-[#333]"
                      }`}
                      onClick={() => handleViewChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTeamManagement && (
        <div className="m-3 p-4 bg-gray-50 border border-gray-200 ">
          <div className="text-center mb-4">
            <h3 className="font-poppins font-semibold text-[18px] text-[#222] mb-1">
              Programme des Workers
            </h3>
            <p className="font-nunito text-[14px] text-[#333]">
              Plannings et assignations de l'équipe
            </p>
          </div>

          <div className="bg-white border border-gray-200 p-3 rounded">
            <h4 className="font-poppins font-medium text-[15px] text-[#444] mb-2">
              Workers Assignés
            </h4>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {localEvents
                .filter(
                  (event) =>
                    event.assignedWorkers && event.assignedWorkers.length > 0,
                )
                .slice(0, 5)
                .map((event) => (
                  <div key={event.id} className="border border-gray-200 p-2">
                    <div className="font-poppins font-bold text-[16px] text-[#111] mb-1">
                      {format(event.start, "dd/MM HH:mm")}
                    </div>
                    <div className="font-nunito text-[14px] text-[#333] mb-1 truncate">
                      {event.title}
                    </div>
                    <div>
                      <span className="font-poppins font-medium text-[15px] text-[#444]">
                        Workers:
                      </span>
                      <div className="mt-0.5">
                        {event.assignedWorkers.map((worker, index) => (
                          <div
                            key={index}
                            className="font-nunito text-[14px] text-green-600"
                          >
                            • {worker}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {localEvents.filter(
              (event) =>
                event.assignedWorkers && event.assignedWorkers.length > 0,
            ).length === 0 && (
              <div className="text-center py-4 font-nunito text-[14px] text-[#333]">
                Aucun worker assigné
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white mt-2 shadow-sm border border-gray-200 overflow-hidden ">
        {currentView === "day" ? (
          <div
            style={{ height: "70vh", minHeight: "500px", overflow: "hidden" }}
          >
            <CustomDayView />
          </div>
        ) : (
          <Calendar
            localizer={localizer}
            events={getFilteredEvents()}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "70vh", minHeight: "500px" }}
            views={["week", "agenda", "month"]}
            view={currentView}
            onView={setCurrentView}
            date={currentDate}
            onNavigate={setCurrentDate}
            culture="fr"
            messages={{
              week: "Semaine",
              month: "Mois",
              today: "Aujourd'hui",
              previous: "Précédent",
              next: "Suivant",
            }}
            toolbar={false}
            showMultiDayTimes={currentView === "week"}
            min={getMinTime()}
            max={getMaxTime()}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable={true}
            components={{
              event: CustomEvent,
              ...(currentView === "month"
                ? {
                    month: {
                      dateHeader: ({ date, label }) => (
                        <CustomMonthView date={date} />
                      ),
                    },
                  }
                : {}),
            }}
            eventPropGetter={customEventPropGetter}
            tooltipAccessor={(event) => {
              if (event.isClosure) {
                return `Fermé\n${event.resource?.reason || "Fermeture"}`;
              }
              if (event.isBreak) {
                return `Pause\n${event.resource?.duration || "Pause déjeuner"}`;
              }
              const workersInfo =
                event.assignedWorkers?.length > 0
                  ? `\nWorkers: ${event.assignedWorkers.join(", ")}`
                  : "\nAucun worker assigné";
              return `${event.title}\nStatut: ${event.status}\nDurée: ${
                event.resource?.estimatedDuration || 30
              } min${workersInfo}`;
            }}
          />
        )}
      </div>
    </div>
  );
}
